// ABOUTME: The eight weather vignettes as data, ported element by element from the weather response reference.
// ABOUTME: Each element is positioned in the reference's scene box (850 x 370), so percentages are fractions of that box.

import type { SxProps, Theme } from '@mui/material';
import { CROSSING_WEATHER_VIGNETTE as T } from '../../../../theme/crossingTokens';
import type { WeatherVignetteDef } from './weatherVignetteTypes';

const abs = (style: Record<string, string | number>): SxProps<Theme> => ({ position: 'absolute' as const, ...style });

const PILL = '999px';

// Under reduced motion an animated element is drawn in its `still` pose: for each property the animation sets, the
// keyframe stop nearest the reference's settled frame (the prototype's frozen "mirror" frame: 64% of a 5 s loop, 44% of
// 7.3 s, 61% of 5.25 s; ties go to the earlier stop), copied verbatim.
export const WEATHER_VIGNETTES: Record<string, WeatherVignetteDef> = {
  got_big_and_loud: {
    elements: [{
      style: abs({ left: '50%', top: '22%', marginLeft: '-78px', width: '156px', height: '74px' }),
      anim: { name: 'wrCloudGrow', seconds: 5, timing: 'ease-in-out' },
      still: { transform: 'scale(1) translateY(0)', opacity: 0.95 },
      children: [{
        style: abs({ inset: 0, opacity: 0.85 }),
        children: [
          { style: abs({ left: 0, bottom: 0, width: '100%', height: '44px', borderRadius: PILL, background: T.cloudGrowBase }) },
          { style: abs({ left: '24%', bottom: '24px', width: '52%', height: '48px', borderRadius: '50%', background: T.cloudGrowMid }) },
          { style: abs({ left: '52%', bottom: '34px', width: '34%', height: '36px', borderRadius: '50%', background: T.cloudGrowTop }) },
        ],
      }],
    }],
  },

  took_the_wheel: {
    elements: [{
      style: abs({ inset: 0 }),
      anim: { name: 'wrPullBack', seconds: 5, timing: 'ease-in-out' },
      still: { transform: 'translateX(26px) rotate(3deg)' },
      children: [
        {
          style: abs({
            left: '50%', top: 'calc(57% - 150px)', marginLeft: '-52px', width: '104px', height: '150px',
            background: T.beamMain, clipPath: 'polygon(40% 0, 60% 0, 100% 100%, 0 100%)',
          }),
          anim: { name: 'wrLock', seconds: 5, timing: 'ease-in-out' },
          still: { opacity: 1, transform: 'scaleX(1.04)' },
        },
        {
          style: abs({ left: '44%', top: '34%', width: '44px', height: '5px', borderRadius: PILL, background: T.streakNear }),
          anim: { name: 'wrStreak', seconds: 5, timing: 'linear' },
          still: { transform: 'translateY(66px)', opacity: 0.55 },
        },
        {
          style: abs({ left: '41%', top: '38%', width: '62px', height: '5px', borderRadius: PILL, background: T.streakMid }),
          anim: { name: 'wrStreak', seconds: 5, timing: 'linear', delay: 'b' },
          still: { transform: 'translateY(66px)', opacity: 0.55 },
        },
        {
          style: abs({ left: '38%', top: '42%', width: '80px', height: '6px', borderRadius: PILL, background: T.streakFar }),
          anim: { name: 'wrStreak', seconds: 5, timing: 'linear', delay: 'c' },
          still: { transform: 'translateY(66px)', opacity: 0.55 },
        },
        {
          style: abs({
            left: '66%', top: 'calc(57% - 120px)', width: '72px', height: '120px', background: T.beamSide,
            clipPath: 'polygon(32% 0, 56% 0, 100% 100%, 0 100%)', transform: 'rotate(15deg)', transformOrigin: '50% 0',
          }),
          anim: { name: 'wrOpenSide', seconds: 5, timing: 'ease-in-out' },
          still: { opacity: 0.7, transform: 'translateX(0)' },
        },
        {
          style: abs({ left: '50%', top: 'calc(57% - 58px)', width: '92px', height: '58px', marginLeft: '-46px', overflow: 'hidden' }),
          children: [
            { style: abs({ left: 0, top: '12px', width: '92px', height: '92px', borderRadius: '50%', border: `3px solid ${T.wheelRim}`, boxSizing: 'border-box' }) },
            { style: abs({ left: '50%', bottom: 0, width: '2.5px', height: '56px', marginLeft: '-1.25px', background: T.wheelSpoke }) },
            { style: abs({ left: '50%', bottom: 0, width: '2.5px', height: '56px', marginLeft: '-1.25px', background: T.wheelSpoke, transform: 'rotate(52deg)', transformOrigin: '50% 100%' }) },
            { style: abs({ left: '50%', bottom: 0, width: '2.5px', height: '56px', marginLeft: '-1.25px', background: T.wheelSpoke, transform: 'rotate(-52deg)', transformOrigin: '50% 100%' }) },
            { style: abs({ left: '50%', bottom: '-7px', width: '14px', height: '14px', marginLeft: '-7px', borderRadius: '50%', border: `2.5px solid ${T.wheelRim}`, boxSizing: 'border-box', background: T.wheelHub }) },
          ],
        },
      ],
    }],
  },

  checked_every_face: {
    elements: [{
      style: abs({ inset: 0 }),
      children: [
        {
          style: abs({ left: '9%', top: '26%', width: '78px', height: '36px', borderRadius: PILL, background: T.cellA }),
          anim: { name: 'wrCellA', seconds: 5, timing: 'ease-in-out' },
          still: { opacity: 0.95 },
          children: [
            { style: abs({ left: '24%', top: '-14px', width: '52%', height: '28px', borderRadius: '50%', background: T.cellATop }) },
          ],
        },
        {
          style: abs({ left: '42%', top: '23%', width: '64px', height: '30px', borderRadius: PILL, background: T.cellB }),
          anim: { name: 'wrCellB', seconds: 5, timing: 'ease-in-out' },
          still: { opacity: 0.4 },
          children: [
            { style: abs({ left: '6%', top: '26px', width: '88%', height: '48px', background: T.cellBRain }) },
          ],
        },
        {
          style: abs({ left: '74%', top: '30%', width: '68px', height: '32px' }),
          anim: { name: 'wrCellC', seconds: 5, timing: 'ease-in-out' },
          still: { opacity: 0.35, transform: 'scale(0.94)' },
          children: [
            { style: abs({ left: 0, top: '2px', width: '100%', height: '3px', borderRadius: '2px', background: T.cellLineA }) },
            { style: abs({ left: '14%', top: '13px', width: '78%', height: '3px', borderRadius: '2px', background: T.cellLineB }) },
            { style: abs({ left: '4%', top: '24px', width: '64%', height: '3px', borderRadius: '2px', background: T.cellLineC }) },
          ],
        },
        {
          style: abs({
            left: '50%', top: 'calc(57% - 108px)', width: '26px', height: '108px', marginLeft: '-13px', background: T.scanBand,
            clipPath: 'polygon(36% 0, 64% 0, 80% 100%, 20% 100%)', transformOrigin: '50% 100%',
          }),
          anim: { name: 'wrScanBand', seconds: 5, timing: 'ease-in-out' },
          still: { opacity: 0.9, transform: 'rotate(32deg)' },
        },
      ],
    }],
  },

  carried_their_feelings: {
    elements: [
      {
        style: abs({ left: '64%', top: '24%', width: '132px', height: '48px' }),
        anim: { name: 'wrFarSquall', seconds: 7.3, timing: 'ease-in-out' },
        still: { opacity: 0.88, transform: 'translateX(-70px)' },
        children: [
          { style: abs({ left: 0, bottom: '14px', width: '100%', height: '28px', borderRadius: PILL, background: T.squallBase }) },
          { style: abs({ left: '22%', bottom: '26px', width: '56%', height: '26px', borderRadius: '50%', background: T.squallTop }) },
          { style: abs({ left: '6%', bottom: '-4px', width: '88%', height: '20px', background: T.squallRainFar }) },
        ],
      },
      {
        style: abs({ left: '50%', top: 'calc(57% - 31px)', width: '66px', height: '26px', marginLeft: '-33px', transformOrigin: '50% 100%' }),
        anim: { name: 'wrMirrorForm', seconds: 7.3, timing: 'ease-in-out' },
        still: { opacity: 0.92, transform: 'scale(1)' },
        children: [
          { style: abs({ left: 0, bottom: '8px', width: '100%', height: '16px', borderRadius: PILL, background: T.squallBase }) },
          { style: abs({ left: '24%', bottom: '16px', width: '50%', height: '15px', borderRadius: '50%', background: T.squallTop }) },
          { style: abs({ left: '6%', bottom: '-3px', width: '88%', height: '11px', background: T.squallRainNear }) },
        ],
      },
    ],
  },

  held_it_in: {
    elements: [
      {
        style: abs({ left: '50%', top: 'calc(57% - 60px)', width: '116px', height: '48px', marginLeft: '-58px', transformOrigin: '50% 100%' }),
        anim: { name: 'wrHeldCloud', seconds: 5.25, timing: 'ease-in-out' },
        still: { opacity: 0.95, transform: 'scale(1.06)' },
        children: [
          { style: abs({ left: 0, bottom: 0, width: '100%', height: '28px', borderRadius: PILL, background: T.heldCloudBase }) },
          { style: abs({ left: '24%', bottom: '16px', width: '50%', height: '28px', borderRadius: '50%', background: T.heldCloudTop }) },
        ],
      },
      {
        style: abs({
          left: '50%', top: 'calc(57% - 70px)', width: '152px', height: '68px', marginLeft: '-76px', borderRadius: '14px',
          border: `3px solid ${T.heldBox}`, boxSizing: 'content-box',
        }),
        anim: { name: 'wrHeldBox', seconds: 5.25, timing: 'ease-in-out' },
        still: { opacity: 1, transform: 'scale(1)' },
      },
    ],
  },

  got_stuck_and_dug_in: {
    elements: [{
      style: abs({ left: '50%', top: 'calc(30% - 56px)', width: '60px', marginLeft: '-30px' }),
      children: [
        {
          style: abs({ left: '34px', top: '52px', width: '2px', transform: 'scaleY(0.7)', transformOrigin: '50% 0', background: T.ropeInk, opacity: 0.85 }),
          anim: { name: 'wrAnchorRope', seconds: 5, timing: 'ease-in-out' },
          still: { height: '62px' },
        },
        {
          style: abs({ left: '25px', top: '50px', fontSize: '15px', lineHeight: 1, color: T.anchorInk }),
          anim: { name: 'wrAnchorSink', seconds: 5, timing: 'ease-in-out' },
          still: { transform: 'translateY(54px)', opacity: 1 },
          children: [
            { style: { display: 'inline-block', transform: 'scale(1.86)', transformOrigin: '50% 0' }, text: '⚓' },
          ],
        },
      ],
    }],
  },

  drifted_into_fog: {
    elements: [
      {
        style: abs({ left: '20%', top: '49%', width: '60px', height: '24px', borderRadius: '14px 14px 0 0', background: T.landmarkNear }),
        anim: { name: 'wrLandmarkFade', seconds: 5, timing: 'ease-in-out' },
        still: { opacity: 0.22 },
      },
      {
        style: abs({ left: '66%', top: '46%', width: '76px', height: '30px', borderRadius: '18px 18px 0 0', background: T.landmarkFar }),
        anim: { name: 'wrLandmarkFade', seconds: 5, timing: 'ease-in-out' },
        still: { opacity: 0.22 },
      },
      {
        style: abs({ left: 0, right: 0, top: '24%', height: '190px', background: T.fogBank }),
        anim: { name: 'wrFogBank', seconds: 5, timing: 'ease-in-out' },
        still: { transform: 'translateX(10px)', opacity: 0.88 },
      },
      { style: abs({ left: '46%', top: '38%', width: '54px', height: '54px', borderRadius: '50%', background: T.fogGlow }) },
    ],
  },

  changed_the_subject: {
    elements: [{
      style: abs({ inset: 0 }),
      anim: { name: 'wrTurnTo', seconds: 5, timing: 'ease-in-out' },
      still: { transform: 'translateX(-34px)' },
      children: [
        {
          style: abs({ left: '34%', top: '20%', width: '130px', height: '56px' }),
          anim: { name: 'wrFrontEdge', seconds: 5, timing: 'ease-in-out' },
          still: { transform: 'translateX(-92px)', opacity: 0.75 },
          children: [
            { style: abs({ left: 0, bottom: '18px', width: '100%', height: '34px', borderRadius: PILL, background: T.frontBase }) },
            { style: abs({ left: '20%', bottom: '34px', width: '58%', height: '30px', borderRadius: '50%', background: T.frontTop }) },
            { style: abs({ left: '8%', bottom: '-4px', width: '84%', height: '24px', background: T.frontRain }) },
          ],
        },
        {
          style: abs({ left: '74%', top: '24%', width: '62px', height: '62px', borderRadius: '50%', background: T.sun }),
          anim: { name: 'wrSunOpen', seconds: 5, timing: 'ease-in-out' },
          still: { opacity: 0.95, transform: 'scale(1)' },
        },
      ],
    }],
  },
};

export function vignetteFor(code: string | undefined): WeatherVignetteDef | undefined {
  return code !== undefined && Object.prototype.hasOwnProperty.call(WEATHER_VIGNETTES, code) ? WEATHER_VIGNETTES[code] : undefined;
}
